import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import {Breakpoint} from '@mui/material/styles';
import * as R from 'ramda';
import {Hidden} from '@mui/material';
import {
  PromotionListItem,
  PromotionType,
  promotionsResponseToListItems,
} from '@practicegenius/domain/src/data/promotions/promotion';
import {UnknownServerData} from '@practicegenius/http-clients-js/src/types';
import {usePhpJs} from '../../utils/php-js';
import {PromotionGridSection} from './components/PromotionGridSection';
import {HighlightedPromotion} from './components/HighlightedPromotion';
import {EmptyPage} from './components/EmptyPage';
import lang from './lang';
import {UnauthenticatedStandardProvider} from '../../components/StandardProvider';

const getPromotionsByType = (
  promotions: PromotionListItem[],
  type: PromotionType
) => R.filter(R.propEq(type, 'type'), promotions);

export const PromotionsDisplay = ({
  promotions,
  name,
  logo,
}: {
  promotions: PromotionListItem[];
  name: string;
  logo: string;
}) => {
  const ongoingPromotions = promotions.filter(p => p.status !== 'past');

  if (promotions.length === 0) {
    return <EmptyPage name={name} logo={logo} />;
  }

  /** This comes sorted by earliest end date, but now that ended giveaways persist
   * we need to filter out past promotions so the highlighted promotion is always
   * an active one. */
  const highlightedPromotion = ongoingPromotions[0];

  const events = getPromotionsByType(promotions, PromotionType.event);
  const offers = getPromotionsByType(promotions, PromotionType.offer);
  const giveaways = getPromotionsByType(promotions, PromotionType.giveaway);

  return (
    <Container
      sx={theme => ({
        paddingTop: theme.spacing(5),
        [theme.breakpoints.down('md')]: {
          paddingTop: theme.spacing(3),
        },
      })}
    >
      {highlightedPromotion && (
        <Hidden mdDown>
          <Box
            mb={10}
            pl={0}
            component="section"
            aria-label="Highlighted Promotion"
          >
            <HighlightedPromotion promotion={highlightedPromotion} />
          </Box>
        </Hidden>
      )}

      {giveaways.length > 0 && (
        <Box mb={3}>
          <PromotionGridSection
            title={lang.main.type.giveaways}
            promotions={giveaways}
          />
        </Box>
      )}

      {events.length > 0 && (
        <Box mb={3}>
          <PromotionGridSection
            title={lang.main.type.events}
            promotions={events}
          />
        </Box>
      )}
      {offers.length > 0 && (
        <Box mb={3}>
          <PromotionGridSection
            title={lang.main.type.offers}
            promotions={offers}
          />
        </Box>
      )}
    </Container>
  );
};

type Practice$PhpJs = {
  practice: {
    practice_name: string;
    logo2: string;
  };
};
const PromotionsApp: React.FC<{
  breakpoint?: Breakpoint;
}> = ({breakpoint}) => {
  const promotions: PromotionListItem[] = usePhpJs(
    ({promotions}: {promotions: UnknownServerData}) =>
      promotionsResponseToListItems({items: promotions} as UnknownServerData)
  );

  const {name, logo} = usePhpJs(({practice}: Practice$PhpJs) => ({
    name: practice.practice_name,
    logo: practice.logo2,
  }));
  return (
    // TODO We don't really have a great way to detect the authenticated vs unauthenticated context in a 'free' way
    //   but we currently don't need anything from that context yet. So just using the unauthenticated context is fine.
    //   When we refactor to Next.Js we'll be forced to find a clever way to provide this 😁
    <UnauthenticatedStandardProvider breakpoint={breakpoint}>
      <PromotionsDisplay name={name} logo={logo} promotions={promotions} />
    </UnauthenticatedStandardProvider>
  );
};

export default PromotionsApp;
